/**
 * Home Page With Daily Prompt
 * The main landing page displaying the daily writing prompt and responses.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { capture } from '../utils/analytics';
import { useAuth } from '../context/AuthenticationContext';
import SinglePostDisplay from '../components/post/SinglePostDisplay';
import WriteNewPostForm from '../components/post/WriteNewPostForm';
import DailyPromptCardWithCountdown from '../components/prompt/DailyPromptCardWithCountdown';
import TodaysRead from '../components/read/TodaysRead';
import './HomePageWithDailyPrompt.css';

const WORD_CATEGORIES = [100, 250, 500];

const HomePageWithDailyPrompt = () => {
  const [prompt, setPrompt] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [postContent, setPostContent] = useState('');
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(250); // Default to 250 words
  const [viewCategory, setViewCategory] = useState(null); // null = all categories
  const [justEnteredContest, setJustEnteredContest] = useState(false); // shown after a prompt response posts
  const [searchParams, setSearchParams] = useSearchParams();
  // Write = respond to the prompt (default); Read = today's featured piece. ?tab=read deep-links.
  const [tab, setTab] = useState(searchParams.get('tab') === 'read' ? 'read' : 'write');
  const { user, token } = useAuth();

  // Key the fetch on the prompt id, not the whole prompt object, so a local prompt
  // tweak (e.g. bumping responseCount after posting) doesn't re-trigger a refetch.
  const promptId = prompt?._id;

  const fetchPosts = useCallback(async () => {
    if (!promptId) return;

    try {
      let url = `/api/posts?promptId=${promptId}`;
      if (viewCategory) {
        url += `&wordCategory=${viewCategory}`;
      }
      const response = await axios.get(url);
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  }, [promptId, viewCategory]);

  const fetchDailyPrompt = useCallback(async () => {
    try {
      const response = await axios.get('/api/prompts/daily');
      const newPrompt = response.data;
      setPrompt(newPrompt);
    } catch (error) {
      console.error('Error fetching daily prompt:', error);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDailyPrompt();
  }, [fetchDailyPrompt]);

  // Track when the daily prompt loads (once per prompt; refetches don't re-fire).
  const viewedPromptIdRef = useRef(null);
  useEffect(() => {
    if (prompt && viewedPromptIdRef.current !== prompt._id) {
      viewedPromptIdRef.current = prompt._id;
      capture('prompt_viewed');
    }
  }, [prompt]);

  useEffect(() => {
    fetchPosts(); // fetchPosts no-ops until a prompt id exists
  }, [fetchPosts]);

  // Arriving from the feed's "Respond to Prompt" link opens the composer straight away.
  useEffect(() => {
    if (prompt && token && searchParams.get('compose')) {
      setShowCreatePost(true);
      setSearchParams({}, { replace: true }); // consume the flag so a refresh won't reopen it
    }
  }, [prompt, token, searchParams, setSearchParams]);

  // ponytail: guard against double-submit — button isn't disabled mid-request, so a
  // double-click/tap would POST twice and create two real posts with distinct _ids.
  const submittingRef = useRef(false);

  const handleCreatePost = useCallback(async (e) => {
    e.preventDefault();
    if (!postContent.trim() || submittingRef.current) return;
    submittingRef.current = true;

    try {
      const response = await axios.post('/api/posts', {
        content: postContent,
        promptId: prompt?._id || null,
        wordCategory: selectedCategory
      });
      setPosts((prev) => [response.data, ...prev]);
      capture('post_created', { wordCategory: selectedCategory, hasPrompt: !!prompt });
      setPostContent('');
      setShowCreatePost(false);
      if (prompt) {
        setJustEnteredContest(true); // every prompt response is automatically a contest entry
        // Bump the card's count locally. Refetching here would race the just-written post
        // and clobber the optimistic prepend with a stale list (post vanishes until reload).
        setPrompt((p) => (p ? { ...p, responseCount: (p.responseCount || 0) + 1 } : p));
      }
    } catch (error) {
      console.error('Error creating post:', error);
    } finally {
      submittingRef.current = false;
    }
  }, [postContent, prompt, selectedCategory]);

  if (loading) {
    return (
      <div className="container loading">
        <div className="loading-spinner"></div>
        <p>Loading today's prompt...</p>
      </div>
    );
  }

  return (
    <div className="container">
      {prompt ? (
        <DailyPromptCardWithCountdown 
          prompt={prompt} 
          responseCount={prompt.responseCount}
          nextRefresh={prompt.nextRefresh}
          onRefresh={fetchDailyPrompt}
          font={prompt.font}
        />
      ) : (
        <div className="prompt-unavailable">
          <h2>Today's Prompt</h2>
          <p>Unable to load today's prompt. Please check your connection and try again.</p>
          <button onClick={fetchDailyPrompt} className="btn btn-primary">
            Retry
          </button>
        </div>
      )}

      {prompt && (
        <div className="category-filter-tabs read-write-switch" role="tablist">
          {['write', 'read'].map((t) => (
            <button
              key={t}
              role="tab"
              aria-selected={tab === t}
              className={`category-tab ${tab === t ? 'active' : ''}`}
              onClick={() => {
                setTab(t);
                capture('home_tab_switched', { tab: t });
              }}
            >
              {t === 'write' ? 'Write' : 'Read'}
            </button>
          ))}
        </div>
      )}

      {tab === 'read' ? <TodaysRead /> : (<>
      {prompt && showCreatePost && token && (
        <div className="create-post-below-prompt">
          <WriteNewPostForm
            user={user}
            prompt={prompt}
            postContent={postContent}
            onContentChange={setPostContent}
            onSubmit={handleCreatePost}
            onCancel={() => setShowCreatePost(false)}
            selectedCategory={selectedCategory}
            onCategoryChange={setSelectedCategory}
          />
        </div>
      )}

      {justEnteredContest && (
        <div className="contest-entered-note">
          Posted! Your piece is automatically entered in{' '}
          <Link to="/contest">this week's contest</Link> — likes move you up the leaderboard.
        </div>
      )}

      <div className="posts-section">
        <div className="posts-section-header">
          <h2>Responses to Today's Prompt</h2>
          {prompt && (
            token ? (
              <button
                className="btn-create-post"
                onClick={() => setShowCreatePost(!showCreatePost)}
              >
                {showCreatePost ? 'Cancel' : '+ Respond to Prompt'}
              </button>
            ) : (
              <Link to="/login" className="btn-create-post">
                Login to Respond
              </Link>
            )
          )}
        </div>
        
        {/* Word Category Filter Tabs */}
        <div className="category-filter-tabs">
          <button
            className={`category-tab ${viewCategory === null ? 'active' : ''}`}
            onClick={() => setViewCategory(null)}
          >
            All
          </button>
          {WORD_CATEGORIES.map((cat) => (
            <button
              key={cat}
              className={`category-tab ${viewCategory === cat ? 'active' : ''}`}
              onClick={() => setViewCategory(cat)}
            >
              {cat} words
            </button>
          ))}
        </div>

        {posts.length === 0 ? (
          <div className="no-posts">
            <p>No responses yet. Be the first to write something creative!</p>
          </div>
        ) : (
          <div className="posts-list">
            {posts.map((post) => (
              <SinglePostDisplay
                key={post._id}
                post={post}
                onUpdate={fetchPosts}
              />
            ))}
          </div>
        )}
      </div>
      </>)}
    </div>
  );
};

export default HomePageWithDailyPrompt;
